import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Star as StarIcon,
  Chat as ChatIcon,
  Bookmark as BookmarkIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

interface Thread {
  id: string;
  threadId: string;
  title?: string;
  createdAt: any;
  lastMessageAt: any;
  isBookmarked: boolean;
}

const Bookmarks: React.FC = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [bookmarkedThreads, setBookmarkedThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUser) return;

    const threadsRef = collection(db, 'users', currentUser.uid, 'threads');
    let q = query(threadsRef, where('isBookmarked', '==', true));
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const threads = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Thread[];
        // Sort threads manually since we can't use the index yet
        threads.sort((a, b) => {
          const aTime = a.lastMessageAt?.toMillis?.() || 0;
          const bTime = b.lastMessageAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
        setBookmarkedThreads(threads);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching bookmarks:', err);
        setError('The bookmarks feature is currently being set up. Please try again in a few minutes.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  const handleRemoveBookmark = async (thread: Thread) => {
    if (!currentUser) return;

    try {
      const threadRef = doc(db, 'users', currentUser.uid, 'threads', thread.id);
      await updateDoc(threadRef, {
        isBookmarked: false,
      });
    } catch (error) {
      console.error('Error removing bookmark:', error);
    }
  };

  const handleThreadClick = (thread: Thread) => {
    navigate('/chat', { state: { threadId: thread.id } });
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    
    try {
      if (timestamp.toDate) {
        return formatDistanceToNow(timestamp.toDate(), { addSuffix: true });
      }
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ color: theme.palette.army.textPrimary, mb: 2 }}>
            Bookmarked Chats
          </Typography>
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: theme.palette.army.main }}>
            <Typography variant="body1" sx={{ color: theme.palette.army.textSecondary }}>
              {error}
            </Typography>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: theme.palette.army.textPrimary, mb: 2 }}>
          Bookmarked Chats
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.army.textSecondary }}>
          Quick access to your saved conversations
        </Typography>
      </Box>

      {bookmarkedThreads.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: theme.palette.army.main }}>
          <BookmarkIcon sx={{ fontSize: 48, color: theme.palette.army.accent, mb: 2 }} />
          <Typography variant="h6" sx={{ color: theme.palette.army.textPrimary, mb: 1 }}>
            No Bookmarked Chats
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.army.textSecondary }}>
            Bookmark important conversations for quick access
          </Typography>
        </Paper>
      ) : (
        <List>
          {bookmarkedThreads.map((thread) => (
            <Paper
              key={thread.id}
              sx={{
                mb: 2,
                bgcolor: theme.palette.army.main,
                '&:hover': {
                  bgcolor: theme.palette.army.shield,
                  cursor: 'pointer',
                },
              }}
              onClick={() => handleThreadClick(thread)}
            >
              <ListItem
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="remove bookmark"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveBookmark(thread);
                    }}
                    sx={{ color: theme.palette.army.accent }}
                  >
                    <BookmarkIcon />
                  </IconButton>
                }
              >
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ChatIcon sx={{ color: theme.palette.army.accent, mr: 1 }} />
                    <Typography variant="h6" sx={{ color: theme.palette.army.textPrimary }}>
                      {thread.title || `Chat ${formatDate(thread.createdAt)}`}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: theme.palette.army.textSecondary }}>
                    Last message {formatDate(thread.lastMessageAt)}
                  </Typography>
                </Box>
              </ListItem>
            </Paper>
          ))}
        </List>
      )}
    </Box>
  );
};

export default Bookmarks; 